import React, { ChangeEvent, useState, useCallback } from "react";
import { AxiosResponse } from "axios";
import AdminNavBar from "./components/AdminNavBar";
import { Form, Link, useNavigate } from "react-router-dom";
import { convertToMegabytes } from "./adminHelper";
import { useDropzone } from "react-dropzone";
import apiService from "../services/api.service";

interface ImageUpload {
  image: File;
  caption: string;
}

const BlogPostForm: React.FC = () => {
  const [blogPost, setBlogPost] = useState({
    title: "",
    cover_image: null as File | null,
    category: "Tutorial",
    social_platform: "Email",
    social_username: "",
    content: "",
    publish_status: "Pending Publish",
    uploaded_images: [] as ImageUpload[],
  });

  const navigate = useNavigate();
  const handleBackToSiteMaterial = () => {
    navigate("/admin/dashboard/", { state: { from: "blogposts" } });
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const imageUploads: ImageUpload[] = acceptedFiles.map((file) => ({
      image: file,
      caption: "caption",
    }));
    setBlogPost((prevBlogPost) => ({
      ...prevBlogPost,
      uploaded_images: [...prevBlogPost.uploaded_images, ...imageUploads],
    }));
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      setBlogPost((prevBlogPost) => ({
        ...prevBlogPost,
        cover_image: file,
      }));
    }
  };

  const handleRemoveImage = (index: number) => {
    setBlogPost((prevBlogPost) => {
      const updatedImages = [...prevBlogPost.uploaded_images];
      updatedImages.splice(index, 1); // Remove the image at the specified index
      return {
        ...prevBlogPost,
        uploaded_images: updatedImages,
      };
    });
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setBlogPost({
      ...blogPost,
      [e.target.name]: e.target.value,
    });
  };

  const handleCaptionChange = (index: number, newCaption: string) => {
    setBlogPost((prevBlogPost) => ({
      ...prevBlogPost,
      uploaded_images: prevBlogPost.uploaded_images.map((upload, i) =>
        i === index ? { ...upload, caption: newCaption } : upload
      ),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("title", blogPost.title);
    if (blogPost.cover_image) {
      formData.append(
        "cover_image",
        blogPost.cover_image,
        blogPost.cover_image.name
      );
    }
    formData.append("category", blogPost.category);
    formData.append("social_platform", blogPost.social_platform);
    formData.append("social_username", blogPost.social_username);
    formData.append("content", blogPost.content);
    formData.append("publish_status", blogPost.publish_status);
    blogPost.uploaded_images.forEach((upload, index) => {
      if (upload.image) {
        try {
          formData.append(
            `uploaded_images_image_${index}`,
            upload.image,
            upload.image.name
          );
        } catch (error) {
          console.error(`Error appending image ${index}:`, error);
        }

        formData.append(`uploaded_images_caption_${index}`, upload.caption);
      }
    });
    const makePostRequest = async () => {
      try {
        await apiService.post<AxiosResponse>(`/blogposts/`, formData);
        alert("Post created successfully");
        setBlogPost({
          title: "",
          cover_image: null,
          category: "Tutorial",
          social_platform: "Email",
          social_username: "",
          content: "",
          publish_status: "Pending Publish",
          uploaded_images: [],
        });
        const fileInput = document.getElementById(
          "formFileInput"
        ) as HTMLInputElement | null;

        if (fileInput) {
          fileInput.value = "";
        }
      } catch (error: any) {
        console.error("Error occured while creating blogpost", error);
        if (error.response && error.response.status === 401) {
          throw new Error("Token expired");
        }
        throw error;
      }
    };

    try {
      await makePostRequest();

      // Reset the file input field
      // const fileInput = document.getElementById(
      //   "formFileInput"
      // ) as HTMLInputElement;
      // if (fileInput) {
      //   fileInput.value = "";
      // }
    } catch (error: any) {
      console.error("Error occurred while creating blog post", error);
      alert("Failed to create post. Please try again.");
    }
  };
  return (
    <>
      <nav className="navbar navbar-dark bg-dark">
        <AdminNavBar />
      </nav>
      <nav id="bc-nav" aria-label="breadcrumb">
        <ol id="bc-ol" className="breadcrumb">
          <li id="bc-books" className="breadcrumb-item">
            <button
              className="inactive-anchor-link"
              onClick={handleBackToSiteMaterial}
            >
              Site Materials
            </button>{" "}
            <span className="greater-than">&#x02AA2;</span>
          </li>
          <Link
            to={`/admin/site-materials/blogposts`}
            className="breadcrumb-item inactive-anchor-link"
          >
            Blogposts <span className="greater-than">&#x02AA2;</span>
          </Link>
          <li id="bc-active" className="breadcrumb-item active">
            create Blogpost
          </li>
        </ol>
      </nav>
      <Form method="post" className="mb-3" onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label"> Title</label>
          <input
            className="form-control"
            type="text"
            name="title"
            value={blogPost.title}
            onChange={handleChange}
            required
          />
        </div>
        <div className="mb-3">
          <label htmlFor="formFileInput" className="form-label">
            Cover Image
          </label>
          <input
            id="formFileInput"
            className="form-control"
            type="file"
            name="cover_image"
            onChange={handleImageChange}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Category</label>
          <select
            className="form-select"
            name="category"
            value={blogPost.category}
            onChange={handleChange}
            required
          >
            <option value="Tutorial">Tutorial</option>
            <option value="News and Updates">News and Updates</option>
            <option value="Tips and Tricks">Tips and Tricks</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Social Plaform</label>
          <select
            className="form-select"
            name="social_platform"
            value={blogPost.social_platform}
            onChange={handleChange}
            required
          >
            <option value="Email">Email</option>
            <option value="Website">Website</option>
            <option value="Twitter(x)">Twitter (X)</option>
            <option value="Instagram">Instagram</option>
            <option value="Github">Github</option>
            <option value="Linkedin">LinkedIn</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Social UserID</label>
          <input
            className="form-control"
            type="text"
            name="social_username"
            value={blogPost.social_username}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mb-3">
          <label>Content Images</label>
          <div id="dropzone" {...getRootProps()}>
            <input {...getInputProps()} />
            <p>Drag 'n' drop image files here, or click to select files</p>
            <div id="dropzone-add"> &#43; </div>
          </div>
        </div>
        <ul>
          {blogPost.uploaded_images.map((upload, index) => (
            <li key={index}>
              {upload.image.name} {convertToMegabytes(upload.image.size) + "MB"}
              <button
                className="btn"
                id="delete-upload-btn"
                type="button"
                onClick={() => handleRemoveImage(index)}
              >
                &#935;
              </button>
              <br />
              <input
                className="form-control"
                type="text"
                name="caption"
                value={upload.caption}
                onChange={(e) => handleCaptionChange(index, e.target.value)}
              />
            </li>
          ))}
        </ul>
        <div className="mb-3">
          <label className="form-label">Content</label>
          <textarea
            className="form-control"
            name="content"
            value={blogPost.content}
            onChange={handleChange}
            required
            rows={28}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            name="publish_status"
            value={blogPost.publish_status}
            onChange={handleChange}
            required
          >
            <option value="Pending Publish"> Pending Publish</option>
            <option value="Publish">Publish</option>
          </select>
        </div>

        <button
          id="create-post-btn"
          className="btn error-page-btn"
          type="submit"
        >
          Create Post
        </button>
      </Form>
    </>
  );
};

export default BlogPostForm;
